import { View, Pressable, Animated, TouchableOpacity, Modal, TextInput, ActivityIndicator, Platform, ScrollView, Image } from 'react-native';
import { useRouter } from 'expo-router';
import * as Linking from 'expo-linking';
import * as Sharing from 'expo-sharing';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { useTheme } from '@/theme';
import { ThemedText, Caption, Body } from '@/components/ui/Text';
import { Button } from '@/components/ui/Button';
import { AppImage } from '@/components/ui/AppImage';
import { formatTripDates, calculateDaysUntil, type Trip as TripType } from '@/data/trips';
import { useLandmarkImage } from '@/hooks/useLandmarkImage';
import { useUserStore } from '@/state/userStore';
import { TripStatusBadge } from './TripStatusBadge';
import type { Trip } from '@/services/trips';
import { memo, useRef, useEffect, useState, useCallback, useMemo } from 'react';
import type { ItineraryItem, ItineraryBookingSnapshot } from '@/services/itinerary';
import type { TripBudgetSnapshot } from '@/types/budget';
import type { Booking } from '@/services/bookings';
import { subscribeToTripBookings } from '@/services/bookings';
import { FlightStatusCard } from '@/components/bookings/FlightStatusCard';
import {
  refreshFlightStatus,
  fetchCheckInLink,
  getFallbackCheckInUrl,
  updateNotificationPreferences as updateFlightNotificationPreferences,
} from '@/services/flightStatus';
import type { FlightNotificationPreferences } from '@/services/bookings';
import { TripRecapHighlights } from './TripRecapHighlights';
import { TripRecapActions } from './TripRecapActions';
import { PostTripAlertSummary } from './PostTripAlertSummary';
import { useTripMemoryPreview } from '@/hooks/useTripMemoryPreview';
import { addTripMemory, fetchTripMemories, type TripMemory } from '@/services/tripMemories';
import { requestDossierExport, buildRecapShareText, buildTripRecapMetrics } from '@/services/dossier';
import { logAnalyticsEvent } from '@/services/analyticsLogger';
import { maybePromptForRating } from '@/services/ratingPrompt';
import { isTripCompleted } from '@/utils/tripStatus';
import { Share, Alert } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import {
  getTripDestinationLabel,
  getTripLandmarkDestination,
  getTripPrimaryDestination,
} from '@/utils/tripDestination';
import { getTripDayContext } from '@/utils/tripDayContext';
import { formatTripItemScheduleLabel } from '@/utils/tripItemScheduleLabel';
import { RecapShareCard } from '@/components/trips/RecapShareCard';
import { TripFullRecapShareCard } from '@/components/trips/TripFullRecapShareCard';
import { buildTripMapSnapshotUrl } from '@/utils/tripMapSnapshot';
import { captureRecapShareImage } from '@/services/recapShareImage';
import { humanizeAsyncError } from '@/utils/asyncErrorMessage';
import { RateTemplateModal } from '@/components/templates/RateTemplateModal';
import { useTripSummary } from '@/hooks/useTripSummary';
import { TripSummaryMapPreview } from '@/components/trips/TripSummaryMapPreview';
import { TripSummaryModal } from '@/components/trips/TripSummaryModal';
import { RecapSharePreviewModal } from '@/components/trips/RecapSharePreviewModal';
import { shareTemplate } from '@/services/templateShare';
import { useToast } from '@/components/ui/ToastProvider';
import { getUserTemplateRating } from '@/services/templateRatings';
import { maybePromptNotificationValue } from '@/services/retentionNotificationPrompt';
import { buildTemplateShareUrl } from '@/navigation/templateDeepLink';
import { acquirePromptLock, releasePromptLock } from '@/services/promptCoordinator';

const TRAVEL_FALLBACKS = [
  // Use only destination-like imagery; omit the tech/workspace photo entirely
  require('../../../assets/travel/travel-2.jpg'),
  require('../../../assets/travel/travel-3.jpg'),
];

type TripCardEnhancedProps = {
  trip: Trip;
  isLast?: boolean;
  onSharePress?: (trip: Trip) => void;
  onActionsPress?: (trip: Trip) => void;
  bookingCount?: number;
  collaboratorCount?: number;
  nextItem?: ItineraryItem | null;
  /** When nextItem is a flight with bookingId, pass the matching booking for the flight dropdown */
  nextEventBooking?: Booking | null;
  budgetSnapshot?: TripBudgetSnapshot;
  entryPoint?: 'default' | 'history' | 'deeplink' | 'rail';
  onRebookPress?: (trip: Trip) => void;
  onOpenItinerary?: (tripId: string, itemId?: string) => void;
  /**
   * Mount-time animations can cause scroll jank when many rows mount/unmount quickly.
   * Disable for long lists or when virtualization is aggressive.
   */
  enableEntranceAnimation?: boolean;
};

const formatTime = (time?: string) => {
  if (!time) return null;
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  if (Number.isNaN(hour)) return null;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes ?? '00'} ${ampm}`;
};

const LabelChip = ({
  icon,
  text,
  color,
  background,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  text: string;
  color: string;
  background: string;
}) => (
  <View
    style={{
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: 8,
      paddingVertical: 4,
      borderRadius: 999,
      backgroundColor: background,
    }}
  >
    <Ionicons name={icon} size={11} color={color} />
    <Caption style={{ fontSize: 11, color }}>{text}</Caption>
  </View>
);

function TripCardEnhancedComponent({
  trip,
  isLast = false,
  onSharePress,
  onActionsPress,
  bookingCount,
  collaboratorCount,
  nextItem,
  nextEventBooking: nextEventBookingProp,
  budgetSnapshot,
  entryPoint = 'default',
  onRebookPress,
  onOpenItinerary,
  enableEntranceAnimation = true,
}: TripCardEnhancedProps) {
  const theme = useTheme();
  const { showSuccess } = useToast();
  const router = useRouter();
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const [flightDropdownExpanded, setFlightDropdownExpanded] = useState(false);
  const [fetchedBooking, setFetchedBooking] = useState<Booking | null>(null);
  const isNextEventFlight = nextItem?.category === 'flight' && nextItem?.bookingId;
  // Back-compat: older trip docs may be missing `owner`. In that case, treat a listed member as the owner
  // for UI affordances (edit/actions), matching the server-side permission check in `updateTrip`.
  const isLegacyMemberOwner =
    !trip.owner && Boolean(firebaseUser?.uid) && Array.isArray(trip.members) && trip.members.includes(firebaseUser!.uid);
  const isOwner = trip.owner === firebaseUser?.uid || isLegacyMemberOwner;
  const primaryDestination = getTripPrimaryDestination(trip);
  const landmarkDestination = getTripLandmarkDestination(trip);
  const destinationLabel = getTripDestinationLabel(trip);
  const primaryDestinationMeta = trip.destinations?.[0];
  
  const daysUntil = calculateDaysUntil(trip.startDate);
  const tripDayContext = getTripDayContext(trip);
  const isActiveTrip = tripDayContext.state === 'active';
  const tripDayNumber = tripDayContext.currentDay;
  const formattedDates = formatTripDates(trip.startDate, trip.endDate);
  const destinationIdentityKey =
    primaryDestinationMeta?.placeId ||
    primaryDestinationMeta?.locationId ||
    null;
  const { imageUrl } = useLandmarkImage(landmarkDestination, trip.landmarkImage ?? null, {
    identityKey: destinationIdentityKey,
    countryName: primaryDestinationMeta?.country,
  });
  const [heroRemoteFailed, setHeroRemoteFailed] = useState(false);
  useEffect(() => {
    setHeroRemoteFailed(false);
  }, [imageUrl]);
  const fallbackIndex = ((primaryDestination || trip.name || '').length + 1) % TRAVEL_FALLBACKS.length;
  const travelFallbackImage = TRAVEL_FALLBACKS[fallbackIndex];
  
  const collaboratorDisplayCount = collaboratorCount ?? (trip.collaborators?.length ?? 0);
  const hasCollaborators = collaboratorDisplayCount > 0;
  const nextSnapshot: ItineraryBookingSnapshot | null = nextItem?.bookingSnapshot || null;
  const isCompleted = isTripCompleted(trip);
  const { preview: memoryPreview, refresh: refreshMemoryPreview, loading: memoryPreviewLoading } = useTripMemoryPreview(
    trip.id,
    isCompleted,
  );
  const [memoryModalVisible, setMemoryModalVisible] = useState(false);
  const [memoryGalleryVisible, setMemoryGalleryVisible] = useState(false);
  const [memoryGalleryLoading, setMemoryGalleryLoading] = useState(false);
  const [memoryGalleryItems, setMemoryGalleryItems] = useState<TripMemory[]>([]);
  const [memoryNote, setMemoryNote] = useState('');
  const [memoryPhotoUri, setMemoryPhotoUri] = useState<string | null>(null);
  const [memorySaving, setMemorySaving] = useState(false);
  const [dossierExporting, setDossierExporting] = useState(false);
  const [templateRatingVisible, setTemplateRatingVisible] = useState(false);
  const [templateRated, setTemplateRated] = useState(false);
  const autoTemplatePromptShownRef = useRef(false);
  const autoTemplatePromptActiveRef = useRef(false);
  const memoryNudgeShownRef = useRef(false);
  const [summaryModalVisible, setSummaryModalVisible] = useState(false);
  const [sharePreviewVisible, setSharePreviewVisible] = useState(false);
  const [sharePreviewInSummary, setSharePreviewInSummary] = useState(false);
  const [sharePreviewUri, setSharePreviewUri] = useState<string | null>(null);
  const [sharePreparing, setSharePreparing] = useState(false);
  const recapShareCardRef = useRef<View>(null);
  const fullRecapShareCardRef = useRef<View>(null);
  const viewStartRef = useRef<number | null>(null);
  const imageParallax = useRef(new Animated.Value(0)).current;
  const recapMetrics = useMemo(
    () =>
      buildTripRecapMetrics(trip, budgetSnapshot, {
        memoryCount: memoryPreview?.count ?? 0,
        memorySnippet: memoryPreview?.latestSnippet,
        placesVisitedCount: (trip.destinations?.length ?? (trip.destination ? 1 : 0)) || 1,
      }),
    [trip, budgetSnapshot, memoryPreview?.count, memoryPreview?.latestSnippet],
  );
  const tripSummary = useTripSummary(trip, {
    budgetSnapshot,
    memoryPreview,
    coverImage: imageUrl,
    enabled: isCompleted,
  });
  const recapMapSnapshotUrl = useMemo(
    () => buildTripMapSnapshotUrl(tripSummary.mappedPlaces, { width: 900, height: 520 }),
    [tripSummary.mappedPlaces],
  );

  useEffect(() => {
    if (!isCompleted) {
      return;
    }
    viewStartRef.current = Date.now();
    return () => {
      if (viewStartRef.current) {
        logAnalyticsEvent('trip_completed_view', {
          tripId: trip.id,
          entryPoint,
          viewDurationMs: Date.now() - viewStartRef.current,
        });
      }
    };
  }, [isCompleted, trip.id, entryPoint]);

  useEffect(() => {
    if (!firebaseUser?.uid || !trip.templateId) {
      setTemplateRated(false);
      return;
    }
    let isMounted = true;
    void getUserTemplateRating(trip.templateId, firebaseUser.uid)
      .then((existingRating) => {
        if (!isMounted) return;
        setTemplateRated(Boolean(existingRating));
      })
      .catch(() => {
        if (!isMounted) return;
        setTemplateRated(false);
      });
    return () => {
      isMounted = false;
    };
  }, [firebaseUser?.uid, trip.templateId, trip.id]);

  useEffect(() => {
    if (!isCompleted || !trip.templateId || templateRated || autoTemplatePromptShownRef.current) {
      return;
    }
    const acquired = acquirePromptLock('template_rating');
    if (!acquired) {
      return;
    }
    autoTemplatePromptShownRef.current = true;
    autoTemplatePromptActiveRef.current = true;
    setTemplateRatingVisible(true);
    void logAnalyticsEvent('template_rating_prompt_shown', {
      templateId: trip.templateId,
      tripId: trip.id,
      source: 'trip_card_completed',
    });
  }, [isCompleted, trip.templateId, trip.id, templateRated]);

  useEffect(() => {
    if (!isCompleted || memoryPreviewLoading || memoryNudgeShownRef.current) {
      return;
    }
    if (!memoryPreview || memoryPreview.count > 0) {
      return;
    }
    if (!acquirePromptLock('memory_nudge', 45_000)) {
      return;
    }
    memoryNudgeShownRef.current = true;
    Alert.alert(
      'Add one memory from this trip',
      'Save a quick highlight now so your recap card captures what made this trip special.',
      [
        { text: 'Later', style: 'cancel', onPress: () => releasePromptLock('memory_nudge') },
        {
          text: 'Add memory',
          onPress: () => {
            releasePromptLock('memory_nudge');
            handleOpenMemoryModal();
          },
        },
      ],
    );
  }, [isCompleted, memoryPreviewLoading, memoryPreview?.count, memoryPreview]);

  // Fetch booking when next event is a flight (for flight dropdown)
  useEffect(() => {
    if (!isNextEventFlight || nextEventBookingProp) {
      setFetchedBooking(null);
      return;
    }
    const unsub = subscribeToTripBookings(trip.id, (bookings) => {
      const match = nextItem!.bookingId
        ? bookings.find((b) => b.id === nextItem!.bookingId)
        : null;
      setFetchedBooking(match ?? null);
    });
    return () => unsub();
  }, [trip.id, nextItem?.bookingId, isNextEventFlight, nextEventBookingProp]);

  const nextEventBooking = nextEventBookingProp ?? fetchedBooking;
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [updatingPrefs, setUpdatingPrefs] = useState(false);

  const handleRefreshFlightStatus = useCallback(async () => {
    if (!trip.id || !nextEventBooking?.id) return;
    setRefreshingStatus(true);
    try {
      await refreshFlightStatus(nextEventBooking.id, trip.id);
    } catch (error) {
      console.error('Error refreshing flight status:', error);
      Alert.alert('Unable to refresh status', humanizeAsyncError(error, 'Please try again shortly.'));
    } finally {
      setRefreshingStatus(false);
    }
  }, [trip.id, nextEventBooking?.id]);

  const handleOpenCheckIn = useCallback(async () => {
    if (!trip.id || !nextEventBooking?.id) return;
    setCheckInLoading(true);
    try {
      let link: string | null = null;
      try {
        link = await fetchCheckInLink(nextEventBooking.id, trip.id);
      } catch {
        link = null;
      }
      if (!link) {
        const code = nextEventBooking.airlineCode ?? (nextEventBooking.provider ? nextEventBooking.provider.replace(/\d+/g, '').trim() : undefined);
        link = getFallbackCheckInUrl(code);
      }
      if (link) {
        await Linking.openURL(link);
      } else {
        Alert.alert('Error', 'Could not open check-in. Try your airline’s website.');
      }
    } catch {
      const fallback = getFallbackCheckInUrl(nextEventBooking?.airlineCode ?? (nextEventBooking?.provider ? nextEventBooking.provider.replace(/\d+/g, '').trim() : undefined));
      if (fallback) {
        try {
          await Linking.openURL(fallback);
        } catch {
          Alert.alert('Error', 'Could not open check-in.');
        }
      } else {
        Alert.alert('Error', 'Could not open check-in.');
      }
    } finally {
      setCheckInLoading(false);
    }
  }, [trip.id, nextEventBooking?.id, nextEventBooking?.airlineCode, nextEventBooking?.provider]);

  const handleUpdateFlightPrefs = useCallback(
    async (prefs: FlightNotificationPreferences) => {
      if (!trip.id || !nextEventBooking?.id) return;
      setUpdatingPrefs(true);
      try {
        await updateFlightNotificationPreferences(nextEventBooking.id, trip.id, prefs);
      } catch (error) {
        console.error('Error updating flight notification preferences:', error);
        Alert.alert('Update failed', humanizeAsyncError(error, 'Could not update preferences. Please try again.'));
      } finally {
        setUpdatingPrefs(false);
      }
    },
    [trip.id, nextEventBooking?.id],
  );

  const handleOpenPlan = useCallback(() => {
    if (onOpenItinerary) {
      onOpenItinerary(trip.id, nextItem?.id);
    } else {
      router.push({ pathname: '/(tabs)/plan', params: { tripId: trip.id, itemId: nextItem?.id } });
    }
  }, [onOpenItinerary, trip.id, nextItem?.id]);

  const formatNextLabel = () => {
    if (!nextItem) return null;
    return formatTripItemScheduleLabel(nextItem, tripDayContext.timeZone, trip);
  };
  
  const renderMetaChip = (
    icon: keyof typeof Ionicons.glyphMap,
    label: string,
    tone: 'default' | 'muted' | 'positive' | 'inverse' = 'default',
  ) => {
    const palette =
      tone === 'positive'
        ? { bg: theme.colors.success + '18', color: theme.colors.success }
        : tone === 'muted'
          ? { bg: theme.colors.primary + '08', color: theme.colors.textSecondary }
          : tone === 'inverse'
            ? { bg: 'rgba(0,0,0,0.35)', color: '#fff' }
            : { bg: theme.colors.primary + '12', color: theme.colors.primary };

    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing(0.5),
          paddingHorizontal: theme.spacing(1),
          paddingVertical: theme.spacing(0.5),
          borderRadius: theme.radii.pill,
          backgroundColor: palette.bg,
        }}
      >
        <Ionicons name={icon} size={13} color={palette.color as string} />
        <Caption style={{ fontSize: 12, fontWeight: '700', color: palette.color as string }}>
          {label}
        </Caption>
      </View>
    );
  };
  
  // Animation for card entrance
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(0.95)).current;

  useEffect(() => {
    if (!enableEntranceAnimation) {
      // Jump to final state without animating to avoid mount-time work for long lists.
      fadeAnim.setValue(1);
      scaleAnim.setValue(1);
      return;
    }
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.spring(scaleAnim, {
        toValue: 1,
        tension: 100,
        friction: 8,
        useNativeDriver: true,
      }),
    ]).start();
  }, [enableEntranceAnimation, fadeAnim, scaleAnim]);

  const handlePress = () => {
    router.push({
      pathname: '/(tabs)/plan',
      params: { tripId: trip.id },
    });
  };

  const handleSharePress = (event?: any) => {
    event?.stopPropagation?.();
    onSharePress?.(trip);
  };

  const handleActionsPress = (event?: any) => {
    event?.stopPropagation?.();
    onActionsPress?.(trip);
  };

  const handleOpenMemoryModal = () => {
    if (!firebaseUser?.uid) {
      Alert.alert('Sign in required', 'Please sign in to add trip memories.');
      return;
    }
    setMemoryModalVisible(true);
  };

  const handleOpenMemoryGallery = () => {
    if (!firebaseUser?.uid) {
      Alert.alert('Sign in required', 'Please sign in to view trip memories.');
      return;
    }
    setMemoryGalleryVisible(true);
    setMemoryGalleryLoading(true);
    void fetchTripMemories(trip.id, { limitCount: 100, newestFirst: true })
      .then((memories) => setMemoryGalleryItems(memories))
      .catch(() => setMemoryGalleryItems([]))
      .finally(() => setMemoryGalleryLoading(false));
  };

  const handleOpenMemoryDetail = (memory: TripMemory) => {
    const memoryText = memory.text?.trim() || 'Photo memory';
    const actions: Array<{ text: string; style?: 'cancel' | 'default'; onPress?: () => void }> = [];
    if (memory.photoUrl) {
      actions.push({
        text: 'View full image',
        onPress: () => {
          void Linking.openURL(memory.photoUrl!);
        },
      });
    }
    actions.push({
      text: 'Add another memory',
      onPress: () => {
        setMemoryGalleryVisible(false);
        handleOpenMemoryModal();
      },
    });
    actions.push({ text: 'Close', style: 'cancel' });
    Alert.alert('Trip memory', memoryText, actions);
  };

  const handlePickMemoryPhoto = async () => {
    const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!permission.granted) {
      Alert.alert(
        'Permission needed',
        'Allow photo library access to attach images. You can continue without a photo.',
        [
          { text: 'Not now', style: 'cancel' },
          { text: 'Open Settings', onPress: () => { void Linking.openSettings(); } },
        ],
      );
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.85,
    });
    if (!result.canceled && result.assets?.length) {
      setMemoryPhotoUri(result.assets[0].uri);
    }
  };

  const handleAddMemory = async () => {
    if (!firebaseUser?.uid) {
      Alert.alert('Sign in required', 'Please sign in to add trip memories.');
      return;
    }
    if (!memoryNote.trim() && !memoryPhotoUri) {
      Alert.alert('Add a note or photo', 'Write a quick note or attach a photo to save this memory.');
      return;
    }
    setMemorySaving(true);
    try {
      await addTripMemory(trip.id, firebaseUser.uid, {
        text: memoryNote.trim(),
        photoUri: memoryPhotoUri ?? undefined,
      });
      logAnalyticsEvent('trip_memory_added', {
        tripId: trip.id,
        hasPhoto: Boolean(memoryPhotoUri),
      });
      await refreshMemoryPreview();
      if (memoryGalleryVisible) {
        const memories = await fetchTripMemories(trip.id, { limitCount: 100, newestFirst: true });
        setMemoryGalleryItems(memories);
      }
      setMemoryNote('');
      setMemoryPhotoUri(null);
      setMemoryModalVisible(false);
    } catch (error) {
      Alert.alert('Unable to save memory', error instanceof Error ? error.message : 'Please try again.');
    } finally {
      setMemorySaving(false);
    }
  };

  const handleDownloadDossier = async () => {
    if (!firebaseUser?.uid) {
      Alert.alert('Sign in required', 'Please sign in to download the dossier.');
      return;
    }
    setDossierExporting(true);
    const role = isOwner ? 'owner' : 'collaborator';
    try {
      const response = await requestDossierExport(trip.id, 'pdf');
      logAnalyticsEvent('trip_dossier_export', {
        tripId: trip.id,
        channel: 'pdf',
        role,
        status: response.status === 'failed' ? 'failed' : 'completed',
      });
      logAnalyticsEvent('trip_export_audit', {
        tripId: trip.id,
        actor: firebaseUser.uid,
        medium: 'client',
      });
      if (response.downloadUrl) {
        await Linking.openURL(response.downloadUrl);
      } else {
        Alert.alert('Dossier requested', 'We will email you a link once the dossier is ready.');
      }
      void maybePromptForRating({
        trigger: 'trip_dossier_export_success',
        source: 'trip_recap_dossier',
        tripId: trip.id,
        deferMs: 350,
      });
    } catch (error) {
      logAnalyticsEvent('trip_dossier_export', {
        tripId: trip.id,
        channel: 'pdf',
        role,
        status: 'failed',
      });
      const errorMessage = error instanceof Error ? error.message : '';
      const errorCode = typeof error === 'object' && error && 'code' in error
        ? String((error as { code?: unknown }).code ?? '')
        : '';
      const isMissingCallable =
        errorMessage.toLowerCase().includes('not-found') ||
        errorCode.toLowerCase().includes('not-found');
      if (isMissingCallable) {
        Alert.alert(
          'Dossier unavailable',
          "Dossier export isn't available yet in this environment. Please try again after the next backend deployment.",
        );
      } else {
        Alert.alert('Download failed', errorMessage || 'Please try again.');
      }
    } finally {
      setDossierExporting(false);
    }
  };

  const handleShareRecap = async () => {
    const fromSummaryModal = summaryModalVisible;
    if (fromSummaryModal) {
      setSharePreviewInSummary(true);
    } else {
      setSharePreviewVisible(true);
    }
    setSharePreparing(true);
    setSharePreviewUri(null);
    try {
      if (fromSummaryModal && recapMapSnapshotUrl) {
        await Image.prefetch(recapMapSnapshotUrl);
      }
      const captureRef = fromSummaryModal ? fullRecapShareCardRef : recapShareCardRef;
      const recapImageUri = await captureRecapShareImage(captureRef, {
        delayMs: fromSummaryModal ? 800 : 450,
      });
      setSharePreviewUri(recapImageUri);
    } catch (error) {
      setSharePreviewVisible(false);
      setSharePreviewInSummary(false);
      if (error instanceof Error && error.message.includes('User did not share')) {
        return;
      }
      const tripDeepLink = Linking.createURL('/(tabs)', { queryParams: { tripId: trip.id } });
      const templateShareUrl = trip.templateId
        ? buildTemplateShareUrl({ templateId: trip.templateId, source: 'recap_share' })
        : undefined;
      const recapText = buildRecapShareText(
        trip,
        budgetSnapshot,
        memoryPreview?.latestSnippet,
        templateShareUrl,
      );
      try {
        await Share.share({
          message: recapText,
          title: `Trip recap: ${trip.name}`,
          url: tripDeepLink,
        });
        logAnalyticsEvent('trip_memory_shared', {
          tripId: trip.id,
          channel: 'share-sheet',
        });
        const notificationPromptShown = await maybePromptNotificationValue({
          moment: 'trip_completed',
          source: 'trip_recap_share_fallback',
        });
        if (!notificationPromptShown) {
          void maybePromptForRating({
            trigger: 'trip_recap_share_success',
            source: 'trip_recap_share_fallback',
            tripId: trip.id,
            deferMs: 300,
          });
        }
      } catch (fallbackError) {
        if (fallbackError instanceof Error && fallbackError.message.includes('User did not share')) {
          return;
        }
        Alert.alert('Unable to share recap', fallbackError instanceof Error ? fallbackError.message : 'Please try again.');
      }
    } finally {
      setSharePreparing(false);
    }
  };

  const handleCancelSharePreview = () => {
    if (sharePreparing) return;
    setSharePreviewVisible(false);
    setSharePreviewInSummary(false);
    setSharePreviewUri(null);
  };

  const handleConfirmSharePreview = async () => {
    if (!sharePreviewUri) return;
    try {
      const canShareFile = await Sharing.isAvailableAsync();
      if (!canShareFile) {
        throw new Error('Image sharing is unavailable in this environment.');
      }
      await Sharing.shareAsync(sharePreviewUri, {
        dialogTitle: `Trip recap: ${trip.name}`,
        mimeType: 'image/jpeg',
        UTI: 'public.jpeg',
      });
      logAnalyticsEvent('trip_memory_shared', {
        tripId: trip.id,
        channel: 'share-sheet',
      });
      const notificationPromptShown = await maybePromptNotificationValue({
        moment: 'trip_completed',
        source: 'trip_recap_share',
      });
      if (!notificationPromptShown) {
        void maybePromptForRating({
          trigger: 'trip_recap_share_success',
          source: 'trip_recap_share',
          tripId: trip.id,
          deferMs: 300,
        });
      }
      setSharePreviewVisible(false);
      setSharePreviewInSummary(false);
      setSharePreviewUri(null);
    } catch (error) {
      if (error instanceof Error && error.message.includes('User did not share')) {
        return;
      }
      Alert.alert('Unable to share recap', error instanceof Error ? error.message : 'Please try again.');
    }
  };

  const handleRebook = () => {
    logAnalyticsEvent('trip_rebook_initiated', {
      tripId: trip.id,
      template: trip.destination,
      destination: trip.destination,
    });
    if (onRebookPress) {
      onRebookPress(trip);
      return;
    }
    router.push('/(tabs)');
  };

  const handleFileExpense = () => {
    router.push({
      pathname: '/(tabs)/wallet',
      params: { tripId: trip.id, fileExpense: 'true' },
    });
  };

  const handleShareSourceTemplate = () => {
    if (!trip.templateId) return;
    void shareTemplate(
      {
        id: trip.templateId,
        title: trip.templateTitle || trip.name,
      },
      {
        source: 'post_use_prompt',
        onCopied: () => showSuccess('Template link copied'),
      },
    );
  };

  const handleUseSourceTemplateAgain = () => {
    if (!trip.templateId) return;
    router.push({
      pathname: '/(tabs)',
      params: {
        openTemplates: '1',
        templateId: trip.templateId,
      },
    });
  };

  const handleTemplateRatingDismiss = () => {
    if (!trip.templateId) return;
    if (autoTemplatePromptActiveRef.current && !templateRated) {
      void logAnalyticsEvent('template_rating_prompt_dismissed', {
        templateId: trip.templateId,
        tripId: trip.id,
        source: 'trip_card_completed',
      });
    }
    autoTemplatePromptActiveRef.current = false;
    releasePromptLock('template_rating');
  };

  const handleTemplateRated = (result: {
    rating: number;
    reviewCount: number;
    submittedRating: number;
    hasReview: boolean;
  }) => {
    setTemplateRated(true);
    if (trip.templateId) {
      void logAnalyticsEvent('template_rating_prompt_completed', {
        templateId: trip.templateId,
        tripId: trip.id,
        rating: result.submittedRating,
        hasReview: result.hasReview,
        source: 'trip_card_completed',
      });
    }
    autoTemplatePromptActiveRef.current = false;
    releasePromptLock('template_rating');
    if (result.submittedRating >= 4) {
      void maybePromptForRating({
        trigger: 'template_rating_success',
        source: 'template_post_completion',
        tripId: trip.id,
        deferMs: 400,
      });
    }
    if (trip.templateId) {
      Alert.alert(
        'Share this template?',
        'Help other travelers by sharing the template you just reviewed.',
        [
          { text: 'Later', style: 'cancel' },
          { text: 'Share template', onPress: handleShareSourceTemplate },
        ],
      );
    }
  };

  const renderNextEventOrRecap = () => {
    if (!isCompleted && nextItem) {
      const showFlightDropdown = isNextEventFlight && nextEventBooking;
      return (
        <View style={{ marginTop: theme.spacing(1), gap: theme.spacing(0.35) }}>
          <Pressable
            onPress={() => showFlightDropdown && setFlightDropdownExpanded((v) => !v)}
            style={({ pressed }) => ({
              opacity: pressed && showFlightDropdown ? 0.8 : 1,
            })}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(0.5) }}>
              <View style={{ flex: 1 }}>
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                  Next event
                </Caption>
                <ThemedText variant="bodyStrong" style={{ fontSize: 15 }}>
                  {nextItem.title}
                </ThemedText>
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                  {formatNextLabel()}
                </Caption>
              </View>
              {showFlightDropdown && (
                <Ionicons
                  name={flightDropdownExpanded ? 'chevron-up' : 'chevron-down'}
                  size={18}
                  color={theme.colors.textSecondary}
                />
              )}
            </View>
            {!flightDropdownExpanded && (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(0.5), marginTop: theme.spacing(0.5) }}>
                {nextSnapshot?.status && (
                  <LabelChip
                    icon="checkmark-circle-outline"
                    text={nextSnapshot.status.replace(/_/g, ' ')}
                    color={theme.colors.primary}
                    background={theme.colors.primary + '15'}
                  />
                )}
                {nextSnapshot?.gate && (
                  <LabelChip
                    icon="airplane-outline"
                    text={`Gate ${nextSnapshot.gate}`}
                    color={theme.colors.textSecondary}
                    background={theme.colors.borderMuted + '30'}
                  />
                )}
                {nextSnapshot?.terminal && (
                  <LabelChip
                    icon="navigate-outline"
                    text={`Terminal ${nextSnapshot.terminal}`}
                    color={theme.colors.textSecondary}
                    background={theme.colors.borderMuted + '30'}
                  />
                )}
              </View>
            )}
          </Pressable>
          {showFlightDropdown && flightDropdownExpanded && (
            <View style={{ marginTop: theme.spacing(1), paddingTop: theme.spacing(1), borderTopWidth: 1, borderTopColor: theme.colors.borderMuted + '50' }}>
              <FlightStatusCard
                booking={nextEventBooking}
                onRefresh={handleRefreshFlightStatus}
                isRefreshing={refreshingStatus}
                onCheckIn={handleOpenCheckIn}
                isCheckInLoading={checkInLoading}
                onUpdatePreferences={handleUpdateFlightPrefs}
                isUpdatingPreferences={updatingPrefs}
              />
              <TouchableOpacity
                onPress={handleOpenPlan}
                style={{ marginTop: theme.spacing(1), alignItems: 'center' }}
              >
                <Caption color={theme.colors.primary} style={{ fontSize: 12, fontWeight: '600' }}>
                  View full itinerary →
                </Caption>
              </TouchableOpacity>
            </View>
          )}
        </View>
      );
    }

    if (isCompleted) {
      return (
        <View style={{ gap: theme.spacing(1) }}>
          <PostTripAlertSummary trip={trip} />
          <TripRecapHighlights
            trip={trip}
            budgetSnapshot={budgetSnapshot}
            memoryPreview={memoryPreview}
            summary={tripSummary}
            onAddMemory={handleOpenMemoryModal}
            onViewMemories={handleOpenMemoryGallery}
          />
          <TripSummaryMapPreview summary={tripSummary} />
          <TripRecapActions
            onViewRecap={() => setSummaryModalVisible(true)}
            onDownloadDossier={handleDownloadDossier}
            onShareRecap={handleShareRecap}
            onRebook={handleRebook}
            onFileExpense={handleFileExpense}
            isDownloading={dossierExporting}
          />
        </View>
      );
    }

    return null;
  };

  const showActionsMenu = isOwner && Boolean(onActionsPress);
  const showShareButton = Boolean(onSharePress);
  const canRateTemplate = Boolean(trip.templateId) && isCompleted;

  return (
    <>
      {isCompleted && (
        <View
          pointerEvents="none"
          style={{ position: 'absolute', left: -12000, top: -12000, opacity: 0 }}
        >
          <View ref={recapShareCardRef} collapsable={false}>
            <RecapShareCard
              metrics={recapMetrics}
              landmarkImageUrl={imageUrl}
              fallbackImageSource={travelFallbackImage}
            />
          </View>
          <View ref={fullRecapShareCardRef} collapsable={false} style={{ marginTop: 40 }}>
            <TripFullRecapShareCard
              summary={tripSummary}
              landmarkImageUrl={imageUrl}
              fallbackImageSource={travelFallbackImage}
              mapSnapshotUrl={recapMapSnapshotUrl}
            />
          </View>
        </View>
      )}
      <Animated.View
        style={{
          opacity: fadeAnim,
          transform: [{ scale: scaleAnim }],
        }}
      >
        <Pressable
          onPress={handlePress}
          onPressIn={() => {
            Animated.spring(scaleAnim, {
              toValue: 0.97,
              useNativeDriver: true,
              tension: 300,
              friction: 10,
            }).start();
            Animated.spring(imageParallax, {
              toValue: -6,
              useNativeDriver: true,
              tension: 120,
              friction: 12,
            }).start();
          }}
          onPressOut={() => {
            Animated.spring(scaleAnim, {
              toValue: 1,
              useNativeDriver: true,
              tension: 300,
              friction: 10,
            }).start();
            Animated.spring(imageParallax, {
              toValue: 0,
              useNativeDriver: true,
              tension: 120,
              friction: 12,
            }).start();
          }}
          style={({ pressed }) => ({
            opacity: pressed ? 0.92 : 1,
          })}
          accessibilityRole={Platform.OS === 'web' ? undefined : 'button'}
          accessibilityLabel={`${trip.name} trip to ${destinationLabel || primaryDestination || 'destination'}, ${formattedDates}`}
          accessibilityHint={`Double tap to view trip details and itinerary for ${trip.name}`}
          accessibilityState={{ 
            disabled: false,
            ...(isCompleted ? { selected: false } : {})
          }}
          {...(Platform.OS === 'web' ? {
            // @ts-ignore - web-specific
            onClick: (e: any) => {
              if (e.target?.closest('button')) {
                return;
              }
              handlePress();
            }
          } : {})}
        >
          <View
            style={{
              borderRadius: theme.radii.xl,
              overflow: 'hidden',
              backgroundColor: theme.colors.surface,
              borderWidth: 1,
              borderColor: theme.colors.borderMuted,
              ...theme.shadows.level0,
            }}
          >
            <View style={{ height: 170, backgroundColor: theme.colors.borderMuted }}>
              <Animated.View style={{ flex: 1, transform: [{ translateY: imageParallax }] }}>
                <AppImage
                  source={
                    heroRemoteFailed || !imageUrl ? travelFallbackImage : { uri: imageUrl }
                  }
                  style={{ width: '100%', height: '100%' }}
                  resizeMode="cover"
                  onError={() => {
                    if (imageUrl) setHeroRemoteFailed(true);
                  }}
                />
              </Animated.View>
              <LinearGradient
                colors={['rgba(0,0,0,0.02)', 'rgba(0,0,0,0.12)', 'rgba(0,0,0,0.35)']}
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 0,
                  bottom: 0,
                  justifyContent: 'flex-end',
                }}
              >
                <View
                  style={{
                    position: 'absolute',
                    top: theme.spacing(1.25),
                    right: theme.spacing(1.25),
                    alignItems: 'flex-end',
                    gap: theme.spacing(0.75),
                    // Ensure actions stay tappable even when long titles wrap underneath.
                    zIndex: 50,
                    elevation: 6,
                  }}
                  pointerEvents="box-none"
                >
                  {(showShareButton || showActionsMenu) && (
                    <View style={{ flexDirection: 'row', gap: theme.spacing(0.65) }}>
                      {showShareButton && (
                        <Pressable
                          onPress={handleSharePress}
                          hitSlop={10}
                          style={({ pressed }) => ({
                            backgroundColor: 'rgba(0,0,0,0.55)',
                            padding: theme.spacing(0.65),
                            borderRadius: theme.radii.full,
                            opacity: pressed ? 0.75 : 1,
                          })}
                          accessibilityRole="button"
                          accessibilityLabel="Share or collaborate on this trip"
                        >
                          <Ionicons name="share-outline" size={16} color="#fff" />
                        </Pressable>
                      )}
                      {showActionsMenu && (
                        <Pressable
                          onPress={handleActionsPress}
                          hitSlop={10}
                          style={({ pressed }) => ({
                            backgroundColor: 'rgba(0,0,0,0.55)',
                            padding: theme.spacing(0.65),
                            borderRadius: theme.radii.full,
                            opacity: pressed ? 0.75 : 1,
                          })}
                          accessibilityRole="button"
                          accessibilityLabel="Open trip actions"
                        >
                          <Ionicons name="ellipsis-horizontal" size={18} color="#fff" />
                        </Pressable>
                      )}
                    </View>
                  )}
                  {!isCompleted && daysUntil !== null && (
                    <View
                      style={{
                        backgroundColor: 'rgba(0,0,0,0.55)',
                        paddingHorizontal: theme.spacing(1),
                        paddingVertical: theme.spacing(0.5),
                        borderRadius: theme.radii.pill,
                        minWidth: 54,
                        alignItems: 'center',
                      }}
                    >
                      <Caption style={{ color: '#fff', fontWeight: '700', fontSize: 12 }}>
                        {isActiveTrip && tripDayNumber !== null
                          ? `Day ${tripDayNumber}`
                          : daysUntil === 0
                            ? 'Today'
                            : daysUntil === 1
                              ? 'Tomorrow'
                              : `${daysUntil}d`}
                      </Caption>
                    </View>
                  )}
                </View>

                <View
                  style={{
                    padding: theme.spacing(2.25),
                    gap: theme.spacing(0.9),
                  }}
                  // Title/metadata should not steal taps from the action buttons when it wraps.
                  pointerEvents="none"
                >
                  <ThemedText
                    variant="h3"
                    style={{
                      color: '#fff',
                      fontWeight: '800',
                      letterSpacing: -0.3,
                      fontSize: 20,
                      lineHeight: 26,
                    }}
                    numberOfLines={2}
                    ellipsizeMode="tail"
                  >
                    {trip.name}
                  </ThemedText>

                  <View 
                    style={{ 
                      flexDirection: 'row', 
                      gap: theme.spacing(0.5),
                      flexWrap: 'wrap',
                      alignItems: 'center', 
                    }}
                  >
                    {renderMetaChip('calendar-outline', formattedDates.split('(')[0].trim(), 'inverse')}
                    {destinationLabel && renderMetaChip('location-outline', destinationLabel, 'inverse')}
                  </View>
                  </View>
              </LinearGradient>
              </View>

            {(hasCollaborators || (!isCompleted && daysUntil !== null)) && (
              <View
                style={{
                  paddingHorizontal: theme.spacing(1.25),
                  paddingVertical: theme.spacing(1),
                  backgroundColor: theme.colors.surface,
                  borderTopWidth: 1,
                  borderTopColor: theme.colors.borderMuted,
                }}
              >
                <View
                  style={{
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: theme.spacing(0.75),
                    alignItems: 'center',
                  }}
                >
                  {!isCompleted && daysUntil !== null && (
                    renderMetaChip(
                      'time-outline',
                      isActiveTrip && tripDayNumber !== null
                        ? `Day ${tripDayNumber} of trip`
                        : daysUntil === 0
                          ? 'Today'
                          : daysUntil === 1
                            ? '1 day away'
                            : `${daysUntil} days away`,
                      'muted'
                    )
                  )}
                  {hasCollaborators && renderMetaChip('people-outline', `${collaboratorDisplayCount} in trip`, 'muted')}
                </View>
              </View>
            )}

            {renderNextEventOrRecap() && (
              <View style={{ padding: theme.spacing(1.25), backgroundColor: theme.colors.surface }}>
                {renderNextEventOrRecap()}
              </View>
            )}

            {canRateTemplate && (
              <View style={{ paddingHorizontal: theme.spacing(1.25), paddingBottom: theme.spacing(1.25), backgroundColor: theme.colors.surface }}>
                <View style={{ gap: theme.spacing(0.75) }}>
                  <Button
                    label={templateRated ? 'Update template rating' : 'Rate this template'}
                    variant="secondary"
                    onPress={() => setTemplateRatingVisible(true)}
                    icon={<Ionicons name="star-outline" size={18} color={theme.colors.text} />}
                  />
                  {isCompleted && (
                    <View style={{ flexDirection: 'row', gap: theme.spacing(0.75) }}>
                      <Button
                        label="Share template"
                        variant="ghost"
                        onPress={handleShareSourceTemplate}
                        icon={<Ionicons name="share-outline" size={16} color={theme.colors.text} />}
                        style={{ flex: 1 }}
                      />
                      <Button
                        label="Use again"
                        variant="ghost"
                        onPress={handleUseSourceTemplateAgain}
                        icon={<Ionicons name="copy-outline" size={16} color={theme.colors.text} />}
                        style={{ flex: 1 }}
                      />
                    </View>
                  )}
                </View>
              </View>
            )}
          </View>
        </Pressable>
      </Animated.View>

      {trip.templateId && isCompleted && (
        <RateTemplateModal
          visible={templateRatingVisible}
          onClose={() => setTemplateRatingVisible(false)}
          onDismiss={handleTemplateRatingDismiss}
          template={{
            id: trip.templateId,
            title: trip.templateTitle || trip.name,
          }}
          tripId={trip.id}
          source={autoTemplatePromptActiveRef.current ? 'post_completion_prompt' : 'trip_card'}
          onRated={handleTemplateRated}
        />
      )}

      {isCompleted && (
        <TripSummaryModal
          visible={summaryModalVisible}
          summary={tripSummary}
          fallbackImageSource={travelFallbackImage}
          onClose={() => {
            setSummaryModalVisible(false);
            setSharePreviewInSummary(false);
            setSharePreviewUri(null);
          }}
          onShare={handleShareRecap}
          sharePreparing={sharePreparing}
          showSharePreview={sharePreviewInSummary}
          sharePreviewUri={sharePreviewUri}
          onCancelSharePreview={handleCancelSharePreview}
          onConfirmShare={handleConfirmSharePreview}
        />
      )}

      <RecapSharePreviewModal
        visible={sharePreviewVisible && !summaryModalVisible}
        imageUri={sharePreviewUri}
        tripName={trip.name}
        preparing={sharePreparing}
        onClose={handleCancelSharePreview}
        onShare={handleConfirmSharePreview}
      />

      <Modal visible={memoryGalleryVisible} transparent animationType="slide">
        <Pressable
          style={{
            flex: 1,
            backgroundColor: theme.colors.overlay,
            justifyContent: 'center',
            padding: theme.spacing(3),
          }}
          onPress={() => setMemoryGalleryVisible(false)}
        >
          <Pressable
            onPress={(e) => e.stopPropagation()}
            style={{
              maxHeight: '78%',
              backgroundColor: theme.colors.surface,
              borderRadius: theme.radii.xl,
              padding: theme.spacing(2),
              gap: theme.spacing(1),
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <ThemedText variant="bodyStrong" style={{ fontSize: 18 }}>
                Trip memories
              </ThemedText>
              <Button label="Close" variant="ghost" onPress={() => setMemoryGalleryVisible(false)} />
            </View>
            {memoryGalleryLoading ? (
              <View style={{ paddingVertical: theme.spacing(2), alignItems: 'center' }}>
                <ActivityIndicator color={theme.colors.primary} />
              </View>
            ) : memoryGalleryItems.length === 0 ? (
              <Caption color={theme.colors.textSecondary}>
                No saved memories yet.
              </Caption>
            ) : (
              <ScrollView contentContainerStyle={{ gap: theme.spacing(1), paddingBottom: theme.spacing(1) }}>
                {memoryGalleryItems.map((memory) => (
                  <Pressable
                    key={memory.id}
                    onPress={() => handleOpenMemoryDetail(memory)}
                    style={{
                      borderWidth: 1,
                      borderColor: theme.colors.borderMuted,
                      borderRadius: theme.radii.lg,
                      padding: theme.spacing(1),
                      backgroundColor: theme.colors.backgroundSecondary,
                      gap: theme.spacing(0.75),
                    }}
                  >
                    {memory.photoUrl ? (
                      <AppImage
                        source={{ uri: memory.photoUrl }}
                        style={{
                          width: '100%',
                          height: 180,
                          borderRadius: theme.radii.md,
                          backgroundColor: theme.colors.surface,
                        }}
                        resizeMode="contain"
                      />
                    ) : null}
                    {memory.text ? (
                      <ThemedText variant="body" style={{ lineHeight: 20 }}>
                        {memory.text}
                      </ThemedText>
                    ) : (
                      <Caption color={theme.colors.textSecondary}>Photo memory</Caption>
                    )}
                    <Caption color={theme.colors.textSecondary}>
                      {new Date(memory.createdAt).toLocaleDateString()}
                    </Caption>
                  </Pressable>
                ))}
              </ScrollView>
            )}
            <Button
              label="Add another memory"
              variant="secondary"
              onPress={() => {
                setMemoryGalleryVisible(false);
                handleOpenMemoryModal();
              }}
            />
          </Pressable>
        </Pressable>
      </Modal>

      <Modal visible={memoryModalVisible} transparent animationType="slide">
        <Pressable
          style={{
            flex: 1,
            backgroundColor: theme.colors.overlay,
            justifyContent: 'center',
            padding: theme.spacing(3),
          }}
          onPress={() => {
            if (!memorySaving) {
              setMemoryModalVisible(false);
            }
          }}
        >
          <Pressable
            onPress={(e) => e.stopPropagation()}
            style={{
              backgroundColor: theme.colors.surface,
              borderRadius: theme.radii.xl,
              padding: theme.spacing(2),
              gap: theme.spacing(1.25),
            }}
          >
            <ThemedText variant="bodyStrong" style={{ fontSize: 18 }}>
              Save a highlight
            </ThemedText>
            <TextInput
              value={memoryNote}
              onChangeText={setMemoryNote}
              placeholder="What stood out about this trip?"
              placeholderTextColor={theme.colors.textSecondary}
              multiline
              style={{
                minHeight: 80,
                borderWidth: 1,
                borderColor: theme.colors.borderMuted,
                borderRadius: theme.radii.md,
                padding: theme.spacing(1.25),
                color: theme.colors.text,
                textAlignVertical: 'top',
              }}
            />
            {memoryPhotoUri ? (
              <View style={{ position: 'relative' }}>
                <AppImage
                  source={{ uri: memoryPhotoUri }}
                  style={{
                    width: '100%',
                    height: 180,
                    borderRadius: theme.radii.md,
                  }}
                  resizeMode="cover"
                />
                <TouchableOpacity
                  onPress={() => setMemoryPhotoUri(null)}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: theme.colors.surface + 'DD',
                    padding: theme.spacing(0.75),
                    borderRadius: theme.radii.full,
                  }}
                >
                  <Ionicons name="close" size={16} color={theme.colors.text} />
                </TouchableOpacity>
              </View>
            ) : (
              <Button
                label="Add photo"
                variant="secondary"
                icon={<Ionicons name="images-outline" size={16} color={theme.colors.primary} />}
                onPress={handlePickMemoryPhoto}
              />
            )}
            <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: theme.spacing(1) }}>
              <Button
                label="Cancel"
                variant="ghost"
                onPress={() => {
                  if (!memorySaving) {
                    setMemoryModalVisible(false);
                  }
                }}
              />
              <Button
                label={memorySaving ? 'Saving...' : 'Save memory'}
                onPress={handleAddMemory}
                disabled={memorySaving}
              />
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

const eqDate = (a?: Date, b?: Date) => {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.getTime() === b.getTime();
};

const eqBudget = (a?: TripBudgetSnapshot, b?: TripBudgetSnapshot) => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.baseCurrency === b.baseCurrency &&
    a.expensesConverted === b.expensesConverted &&
    a.bookingsConverted === b.bookingsConverted &&
    a.lastUpdated === b.lastUpdated
  );
};

const eqNextItem = (a?: ItineraryItem | null, b?: ItineraryItem | null) => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.id === b.id &&
    a.title === b.title &&
    eqDate(a.startDate, b.startDate) &&
    a.startTime === b.startTime
  );
};

const eqTripCore = (a: Trip, b: Trip) => {
  // Only compare fields that affect this card's UI (avoid deep compares).
  const aFirstDest = a.destinations?.[0]?.city;
  const bFirstDest = b.destinations?.[0]?.city;
  const aFirstCountry = a.destinations?.[0]?.country;
  const bFirstCountry = b.destinations?.[0]?.country;
  const aLastDest = a.destinations?.[a.destinations.length - 1]?.city;
  const bLastDest = b.destinations?.[b.destinations.length - 1]?.city;
  const aDestLen = a.destinations?.length ?? 0;
  const bDestLen = b.destinations?.length ?? 0;

  return (
    a.id === b.id &&
    a.name === b.name &&
    eqDate(a.startDate, b.startDate) &&
    eqDate(a.endDate, b.endDate) &&
    a.status === b.status &&
    a.isArchived === b.isArchived &&
    a.destination === b.destination &&
    a.landmarkImage === b.landmarkImage &&
    aDestLen === bDestLen &&
    aFirstDest === bFirstDest &&
    aFirstCountry === bFirstCountry &&
    aLastDest === bLastDest
  );
};

// Memoized component for performance (critical: used as a row in Trips list)
export const TripCardEnhanced = memo(TripCardEnhancedComponent, (prev, next) => {
  return (
    eqTripCore(prev.trip, next.trip) &&
    prev.isLast === next.isLast &&
    prev.bookingCount === next.bookingCount &&
    prev.collaboratorCount === next.collaboratorCount &&
    eqNextItem(prev.nextItem, next.nextItem) &&
    eqBudget(prev.budgetSnapshot, next.budgetSnapshot) &&
    prev.entryPoint === next.entryPoint &&
    prev.enableEntranceAnimation === next.enableEntranceAnimation &&
    prev.onSharePress === next.onSharePress &&
    prev.onActionsPress === next.onActionsPress &&
    prev.onRebookPress === next.onRebookPress
  );
});
